// Month / week calendar grid shared by the leave calendar and Takvimim.
//
// Presentational only: the caller owns the window, the data and what a day
// shows (`renderDay`). The grid owns the Monday-first layout, the weekday
// header (Intl, never literal names — ADR-006), today, weekends and holiday
// shading, so both calendars mark the same day the same way.

import type { ReactNode } from "react";

import { weekdayIndex, type DayKey } from "@/lib/calendar-window";

export interface CalendarGridProps {
  /** Month: `monthCells()` (null = filler). Week: seven day keys. */
  readonly days: readonly (DayKey | null)[];
  readonly variant: "month" | "week";
  readonly caption: string;
  readonly locale: string;
  readonly today: DayKey;
  /** Holiday name for a day, if any; shades the cell and shows `holidayLabel`. */
  readonly holidayOf?: (day: DayKey) => string | undefined;
  readonly holidayLabel?: string;
  readonly renderDay: (day: DayKey) => ReactNode;
}

const utc = (day: DayKey): Date => new Date(`${day}T00:00:00Z`);

export function CalendarGrid({
  days,
  variant,
  caption,
  locale,
  today,
  holidayOf,
  holidayLabel,
  renderDay,
}: CalendarGridProps): React.ReactElement {
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  // 2024-01-01 is a Monday: a fixed anchor for the Monday-first header row.
  const weekdays = Array.from({ length: 7 }, (_, i) => weekdayFmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
  const dayNumberFmt = new Intl.DateTimeFormat(locale, { day: "numeric", timeZone: "UTC" });
  const fullDateFmt = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeZone: "UTC" });

  const weeks: (DayKey | null)[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push([...days.slice(i, i + 7)]);

  // At 360px a column is ~46px and a name chip keeps two letters before the
  // ellipsis. The grid keeps a readable minimum and scrolls inside its card;
  // dot markers were rejected because both callers' chips carry the name.
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a labeled overflow region needs a keyboard scroll target (WCAG 2.1.1)
    <div role="region" aria-label={caption} tabIndex={0} className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--focus-ring)]">
      <table className="w-full min-w-[40rem] table-fixed border-collapse">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-subtle)]">
            {weekdays.map((label, i) => (
              <th
                key={label}
                scope="col"
                className={`py-2 text-center font-sans text-[11px] font-semibold tracking-[0.04em] ${
                  i >= 5 ? "text-[var(--fg-muted)]" : "text-[var(--fg-secondary)]"
                }`}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Calendar weeks have no stable id; the grid index is the key. */}
          {weeks.map((week, wi) => (
            <tr key={wi} className={wi > 0 ? "border-t border-[var(--border-subtle)]" : ""}>
              {week.map((day, di) => {
                if (day === null) {
                  return (
                    <td
                      key={di}
                      className="border-l border-[var(--border-subtle)] bg-[var(--bg-subtle)] first:border-l-0"
                    />
                  );
                }
                const isToday = day === today;
                const weekend = weekdayIndex(day) >= 5;
                const holiday = holidayOf?.(day);
                return (
                  <td
                    key={day}
                    className={`${variant === "week" ? "h-64" : "h-24"} border-l border-[var(--border-subtle)] p-1.5 align-top first:border-l-0 ${
                      holiday !== undefined
                        ? "bg-[var(--state-danger)]/5"
                        : weekend
                          ? "bg-[var(--bg-subtle)]"
                          : "bg-[var(--bg-surface)]"
                    }`}
                  >
                    <div className="mb-1.5 flex items-center justify-between gap-1">
                      <span className="sr-only">
                        {holiday !== undefined ? `${fullDateFmt.format(utc(day))} · ${holiday}` : fullDateFmt.format(utc(day))}
                      </span>
                      <span
                        aria-hidden="true"
                        className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 font-mono text-[12.5px] ${
                          isToday
                            ? "bg-[var(--accent)] font-bold text-[var(--fg-on-accent)]"
                            : weekend
                              ? "font-medium text-[var(--fg-muted)]"
                              : "font-medium text-[var(--fg-secondary)]"
                        }`}
                      >
                        {dayNumberFmt.format(utc(day))}
                      </span>
                      {holiday !== undefined && holidayLabel !== undefined && (
                        <span
                          aria-hidden="true"
                          title={holiday}
                          className="truncate rounded px-1 font-sans text-[10px] font-semibold uppercase text-[var(--state-danger)]"
                        >
                          {holidayLabel}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-[3px]">{renderDay(day)}</div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
